import { Flex, Text } from "@radix-ui/themes";
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo } from "react";
import type { SponsorsReportCreative } from "@/types/sponsors_report";
import { formatCount, formatCtr } from "@/shared/sponsors/stats";
import BasicTable from "../BasicTable";

interface Props {
  creatives: SponsorsReportCreative[];
}

const columnHelper = createColumnHelper<SponsorsReportCreative>();

export default function CreativesTable({ creatives }: Props) {
  const columns = useMemo(
    () => [
      columnHelper.accessor("alt_text", {
        header: "Creative",
        cell: (props) => {
          const creative = props.row.original;
          return (
            <Flex align="center" gap="3">
              {creative.thumbnail_url && (
                <img
                  src={creative.thumbnail_url}
                  alt=""
                  style={{
                    height: 32,
                    width: "auto",
                    maxWidth: 160,
                    borderRadius: 3,
                  }}
                />
              )}
              <Flex direction="column">
                <Text size="2" weight="medium">
                  {creative.alt_text}
                </Text>
                <Text size="1" color="gray">
                  {creative.campaign_name}
                </Text>
              </Flex>
            </Flex>
          );
        },
      }),
      columnHelper.accessor("slot_label", {
        header: "Slot",
        cell: (props) => <Text size="2">{props.getValue()}</Text>,
      }),
      columnHelper.accessor("impressions", {
        header: "Impressions",
        cell: (props) => <Text size="2">{formatCount(props.getValue())}</Text>,
      }),
      columnHelper.accessor("clicks", {
        header: "Clicks",
        cell: (props) => <Text size="2">{formatCount(props.getValue())}</Text>,
      }),
      columnHelper.accessor("ctr", {
        header: "CTR",
        cell: (props) => <Text size="2">{formatCtr(props.getValue())}</Text>,
        sortUndefined: "last",
      }),
    ],
    [],
  );

  const table = useReactTable({
    data: creatives,
    columns,
    enableColumnFilters: false,
    initialState: { sorting: [{ id: "impressions", desc: true }] },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return <BasicTable table={table} />;
}
